import { useEffect, useRef } from 'react';
import { Checkbox } from '@/components/shadcn/checkbox.tsx';
import { Field, FieldGroup, FieldLabel, FieldSet } from '@/components/shadcn/field.tsx';
import { Input } from '@/components/shadcn/input.tsx';
import { Label } from '@/components/shadcn/label.tsx';
import {
  govtIntakeFormLegacyDataLiveToday,
  govtIntakeFormLegacyDataQuality,
  govtIntakeFormReporting,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import { type PropertyLegacyConsolidationData, useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';

export default function PropertiesSubLegacyData({ onValidationChange }: ValidationChange) {
  const { data, updatePropertiesSubSection } = useIntakeForm();
  const raw = data.properties?.legacyConsolidation;
  const legacyData: PropertyLegacyConsolidationData = {
    consolidate: raw?.consolidate ?? '',
    propertyDataLive: raw?.propertyDataLive ?? {},
    numberOfProperties: raw?.numberOfProperties ?? '',
    approxParticipants: raw?.approxParticipants ?? '',
    approxDocumentsFiles: raw?.approxDocumentsFiles ?? '',
    dataQuality: raw?.dataQuality ?? '',
  };

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updateLegacy = (newData: PropertyLegacyConsolidationData) => {
    updatePropertiesSubSection('legacyConsolidation', newData);
  };

  const handleFieldChange = <K extends keyof PropertyLegacyConsolidationData>(field: K, value: PropertyLegacyConsolidationData[K]) => {
    updateLegacy({
      ...legacyData,
      [field]: value,
    });
  };

  const handleConsolidateChange = (value: string) => {
    handleFieldChange('consolidate', value);
  };

  const handleDataLocationChange = (id: string, checked: boolean) => {
    handleFieldChange('propertyDataLive', {
      ...legacyData.propertyDataLive,
      [id]: checked,
    });
  };

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <header className='shrink-0 pt-4'>
        <HeaderAndSub title='Legacy Data (Properties)' subTitle='Understand existing program data and ingestion requirements.' />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='space-y-6 pt-8'>
          <div className='space-x-1'>
            <Label className='block text-sm font-semibold text-gray-900 mb-3'>
              Do you want SEIN Terminal to consolidate existing properties data into the platform?
            </Label>
            <div className='space-y-2 px-1'>
              {govtIntakeFormReporting.map((option) => (
                <Label key={option.id} className='flex items-center gap-2 cursor-pointer'>
                  <Input
                    type='radio'
                    value={option.id}
                    checked={legacyData.consolidate === option.id}
                    onChange={() => handleConsolidateChange(option.id)}
                    className='w-4 h-4 accent-green-600 cursor-pointer'
                  />
                  <span className='text-sm text-gray-700'>{option.title}</span>
                </Label>
              ))}
            </div>
          </div>

          <div className='space-y-3'>
            <Label className='block text-sm font-semibold text-gray-900 mb-4'>Where does data live today?</Label>
            {govtIntakeFormLegacyDataLiveToday.map((item) => {
              const id = `data-live-${item.id}`;
              const isChecked = legacyData.propertyDataLive[item.id] ?? false;

              return (
                <FieldSet key={id}>
                  <FieldGroup>
                    <Field orientation='horizontal'>
                      <Checkbox
                        id={id}
                        checked={isChecked}
                        onCheckedChange={(checked) => handleDataLocationChange(item.id, Boolean(checked))}
                      />
                      <FieldLabel htmlFor={id} className='cursor-pointer select-none'>
                        {item.label}
                      </FieldLabel>
                    </Field>
                  </FieldGroup>
                </FieldSet>
              );
            })}
          </div>

          {(legacyData.consolidate === 'yes' || legacyData.consolidate === 'notSure') && (
            <div className='space-y-8'>
              <div className='space-y-8'>
                <Label className='block text-sm font-semibold text-gray-900 mb-4'>Rough volume estimates</Label>
                <div>
                  <Label className='block text-sm font-medium text-gray-900 mb-1'>Number of properties</Label>
                  <Input
                    type='text'
                    value={legacyData.numberOfProperties}
                    onChange={(e) => handleFieldChange('numberOfProperties', e.target.value)}
                    placeholder='e.g., 5-10'
                    className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                  />
                </div>

                <div>
                  <Label className='block text-sm font-medium text-gray-900 mb-1'>Approx participants</Label>
                  <Input
                    type='text'
                    value={legacyData.approxParticipants}
                    onChange={(e) => handleFieldChange('approxParticipants', e.target.value)}
                    placeholder='e.g., 500-1000'
                    className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                  />
                </div>

                <div>
                  <Label className='block text-sm font-medium text-gray-900 mb-1'>Approx documents/files</Label>
                  <Input
                    type='text'
                    value={legacyData.approxDocumentsFiles}
                    onChange={(e) => handleFieldChange('approxDocumentsFiles', e.target.value)}
                    placeholder='e.g., 100-500'
                    className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                  />
                </div>
              </div>
              <div>
                <Label className='block text-sm font-semibold text-gray-900 mb-3'>Data quality</Label>
                <div className='space-y-2'>
                  {govtIntakeFormLegacyDataQuality.map((option) => (
                    <Label key={option.id} className='flex items-center gap-2 cursor-pointer'>
                      <Input
                        type='radio'
                        value={option.id}
                        checked={legacyData.dataQuality === option.id}
                        onChange={() => handleFieldChange('dataQuality', option.id)}
                        className='w-4 h-4 accent-green-600 cursor-pointer'
                      />
                      <span className='text-sm text-gray-700'>{option.label}</span>
                    </Label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
